import React, { useCallback, useMemo, useRef, useState } from 'react';
import styles from './AnnotatedText.module.css';

/**
 * Text with anchored comments — select a passage, attach a comment to it.
 *
 * The anchor is a pair of character offsets into the *plain text* the caller
 * passes in. That is deliberate: the offsets are stored server-side and have to
 * survive being read back by something other than this component, so they
 * cannot depend on the DOM structure this component happens to render.
 *
 * Getting the offset of a selection is the fiddly part. `window.getSelection()`
 * reports a node and an offset within that node, and this component renders the
 * text as many nodes (one per highlighted or unhighlighted span). So each span
 * carries its own start offset in a data attribute, and the selection's offset
 * is added to it. Walking the DOM to count characters would break the moment
 * the render splits a span differently.
 *
 * Props:
 *   text        — the plain text being annotated
 *   comments    — [{ id, anchor_start, anchor_end, orphaned, resolved_at, ... }]
 *   activeId    — comment to highlight as focused
 *   onSelect    — ({ start, end, text }) when the user selects a passage
 *   onCommentClick — (comment) when a highlight is clicked
 *   readOnly    — disable selection-to-comment
 */
const AnnotatedText = ({
  text = '',
  comments = [],
  activeId = null,
  onSelect,
  onCommentClick,
  readOnly = false,
}) => {
  const containerRef = useRef(null);
  const [pending, setPending] = useState(null);

  // Only anchored, unresolved, non-orphaned comments can be drawn over the
  // text. An orphaned one has offsets that no longer match what it quoted, so
  // highlighting there would point the reader at the wrong words — worse than
  // showing nothing. The sidebar still lists it, flagged.
  const anchored = useMemo(
    () =>
      comments
        .filter(
          (c) =>
            c.anchor_start != null &&
            c.anchor_end != null &&
            !c.orphaned &&
            !c.resolved_at &&
            c.anchor_end <= text.length
        )
        .sort((a, b) => a.anchor_start - b.anchor_start),
    [comments, text.length]
  );

  /**
   * Cut the text into spans at every highlight boundary.
   *
   * Overlapping comments are handled by only advancing past the furthest point
   * already covered — the second comment on an overlapping range still appears
   * in the sidebar, it just does not double-render the shared characters.
   */
  const segments = useMemo(() => {
    const out = [];
    let cursor = 0;
    for (const comment of anchored) {
      const start = Math.max(comment.anchor_start, cursor);
      if (start >= comment.anchor_end) continue;
      if (start > cursor) {
        out.push({ key: `p${cursor}`, start: cursor, text: text.slice(cursor, start) });
      }
      out.push({
        key: comment.id,
        start,
        text: text.slice(start, comment.anchor_end),
        comment,
      });
      cursor = comment.anchor_end;
    }
    if (cursor < text.length) {
      out.push({ key: `p${cursor}`, start: cursor, text: text.slice(cursor) });
    }
    return out;
  }, [anchored, text]);

  const handleMouseUp = useCallback(() => {
    if (readOnly || !onSelect) return;

    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
      setPending(null);
      return;
    }

    const range = selection.getRangeAt(0);
    const root = containerRef.current;
    if (!root || !root.contains(range.commonAncestorContainer)) return;

    // Each rendered span knows where it starts in the source text, so the
    // absolute offset is that base plus the offset within the node. This is
    // what keeps the anchors independent of how the text happens to be split.
    const base = (node) => {
      const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
      const span = el?.closest('[data-offset]');
      return span ? Number(span.dataset.offset) : null;
    };

    const startBase = base(range.startContainer);
    const endBase = base(range.endContainer);
    if (startBase == null || endBase == null) return;

    const start = startBase + range.startOffset;
    const end = endBase + range.endOffset;
    if (end <= start) return;

    const rect = range.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();
    setPending({
      start,
      end,
      text: text.slice(start, end),
      top: rect.bottom - rootRect.top + 6,
      left: Math.max(0, rect.left - rootRect.left),
    });
  }, [onSelect, readOnly, text]);

  const confirm = () => {
    if (pending) onSelect(pending);
    setPending(null);
    window.getSelection()?.removeAllRanges();
  };

  return (
    <div className={styles.wrapper} ref={containerRef}>
      <div className={styles.text} onMouseUp={handleMouseUp}>
        {segments.length === 0 ? (
          <span className={styles.empty}>Nothing submitted yet.</span>
        ) : (
          segments.map((segment) =>
            segment.comment ? (
              <mark
                key={segment.key}
                data-offset={segment.start}
                className={`${styles.highlight} ${
                  activeId === segment.comment.id ? styles.highlightActive : ''
                }`}
                onClick={() => onCommentClick?.(segment.comment)}
                title="Open this comment"
              >
                {segment.text}
              </mark>
            ) : (
              <span key={segment.key} data-offset={segment.start}>
                {segment.text}
              </span>
            )
          )
        )}
      </div>

      {pending && (
        <button
          type="button"
          className={styles.addButton}
          style={{ top: pending.top, left: pending.left }}
          onMouseDown={(e) => e.preventDefault()} // keep the selection alive
          onClick={confirm}
        >
          💬 Comment
        </button>
      )}
    </div>
  );
};

export default AnnotatedText;
